import { money, toMoney } from './money.js'

/**
 * Progress-claim arithmetic shared by the claim editor (preview) and the API (authoritative), as
 * `sales-calc.ts` is for Sales (docs/refactor/projects/04-contracting.md §8.4–8.6, §8.10).
 *
 * Everything is cumulative: figures are rounded once, at the to-date level (round3, half away from
 * zero), and this-period figures are differences of to-date figures, so a series of certificates ends
 * exactly at the contract amounts whatever the rounding on the way.
 */

const D = (v: string | number) => money(v)
const r3 = (v: ReturnType<typeof money>) => toMoney(v)
const max0 = (v: ReturnType<typeof money>) => (v.lessThan(0) ? money(0) : v)

/** Round to 4 decimals (quantities and percentages), half away from zero. */
export function round4(v: string | number): string {
  return D(v).toDecimalPlaces(4, 4 /* ROUND_HALF_UP: away from zero */).toFixed(4)
}

/**
 * Value to date of a claim line. QUANTITY: round3(cumulative quantity × rate). PERCENT:
 * round3(cumulative % × line amount ÷ 100). An override replaces it (the client's figure to the baisa).
 */
export function claimLineValue(line: {
  measureBy: 'QUANTITY' | 'PERCENT'
  rate: string
  amount: string
  cumQuantity: string | null
  cumPercent: string | null
  valueOverride?: string | null
}): string {
  if (line.valueOverride !== undefined && line.valueOverride !== null) return toMoney(line.valueOverride)
  if (line.measureBy === 'QUANTITY') return r3(D(line.cumQuantity ?? '0').times(line.rate))
  return r3(
    D(line.cumPercent ?? '0')
      .times(line.amount)
      .dividedBy(100),
  )
}

/** Measuring refusals of one line (04 §8.4 rule 4): above 100% always; above the quantity per policy. */
export function claimLineIssue(line: {
  measureBy: 'QUANTITY' | 'PERCENT'
  quantity: string
  cumQuantity: string | null
  cumPercent: string | null
}): 'PERCENT_OVER_100' | 'OVER_MEASURE' | 'NEGATIVE' | null {
  if (line.measureBy === 'PERCENT') {
    const p = D(line.cumPercent ?? '0')
    if (p.lessThan(0)) return 'NEGATIVE'
    return p.greaterThan(100) ? 'PERCENT_OVER_100' : null
  }
  const q = D(line.cumQuantity ?? '0')
  if (q.lessThan(0)) return 'NEGATIVE'
  return q.greaterThan(line.quantity) ? 'OVER_MEASURE' : null
}

export type RecoveryMethod = 'PRO_RATA' | 'FIXED_RATE' | 'MANUAL'

/** The terms the deductions read (the contract's snapshot, 04 §8.1). Percentages as decimal strings. */
export interface ClaimTerms {
  /** Original contract sum (OS). */
  originalSum: string
  /** Original sum + approved variations. */
  currentValue: string
  retentionRatePct: string
  /** null: no cap. */
  retentionCapPct: string | null
  retentionCapBasis: 'ORIGINAL' | 'CURRENT'
  /**
   * Advance invoiced, net of VAT, less reductions (A, the net advance); "0" without an advance. Every
   * method's cap, MANUAL's and FIXED_RATE's ceiling, and what a FINAL certificate recovers.
   */
  advance: string
  /**
   * The advance as invoiced, net of VAT, before reductions: PRO_RATA's rate (P5 decision 31). A reduction
   * lowers the cap (`advance`), never the contractual rate, so recovery never runs backwards.
   */
  advanceInvoiced: string
  recovery: RecoveryMethod
  /** FIXED_RATE: recovered rate of each certificate's gross above the threshold. */
  fixedRatePct: string
  /** FIXED_RATE: recovery starts when certified gross passes this % of the threshold basis. */
  startThresholdPct: string
  /** FIXED_RATE threshold basis: the original sum, or the original sum less provisional sums (FIDIC 14.2). */
  thresholdBasis: 'ORIGINAL' | 'ORIGINAL_LESS_PROVISIONAL'
  /** Σ provisional sums of the contract BOQ (for `ORIGINAL_LESS_PROVISIONAL`). */
  provisionalSums: string
  /** The contract's VAT rate, percent ("5.00"). */
  taxRatePct: string
}

export interface ClaimToDate {
  gross: string
  retention: string
  recovery: string
  penalty: string
}

export interface ClaimCalcInput {
  kind: 'INTERIM' | 'FINAL'
  terms: ClaimTerms
  /** Certified gross to date (G): Σ line values to date. */
  grossToDate: string
  /** The latest certified claim's to-date figures (zeros for the first claim). */
  previous: ClaimToDate
  /** Retention released to date (ACTIVE releases). */
  retentionReleased: string
  /** Liquidated damages to date (entered, cumulative). */
  penaltyToDate: string
  /** MANUAL recovery: entered to date (capped at the advance). */
  manualRecoveryToDate?: string | null
  /** The client's own figures, to the baisa (each needs a reason in the API). */
  retentionOverride?: string | null
  recoveryOverride?: string | null
}

export interface ClaimFigure {
  toDate: string
  previous: string
  period: string
}

export interface ClaimCalcResult {
  gross: ClaimFigure
  /** Computed retention to date before any override, and the figure used. */
  retentionComputed: string
  retention: ClaimFigure
  recoveryComputed: string
  recovery: ClaimFigure
  penalty: ClaimFigure
  /** N = Δgross − Δretention − Δrecovery − Δpenalty: the taxable amount of this certificate's invoice. */
  net: string
  /** round3(N × VAT rate ÷ 100). */
  vat: string
  /** N + VAT. */
  total: string
  /** Contract revenue of the period = Δgross − Δpenalty (= N + Δretention + Δrecovery). */
  revenue: string
  /**
   * Refusals (empty: certifiable): 04 §8.5, plus overrides beyond what the contract allows (accounting
   * review P4): recovery to date above the advance, retention to date above the gross to date.
   */
  errors: (
    | 'NEGATIVE_CERTIFICATE'
    | 'RETENTION_BELOW_RELEASED'
    | 'RECOVERY_ABOVE_ADVANCE'
    | 'RETENTION_ABOVE_GROSS'
  )[]
}

/** Retention to date: min(round3(r% × G), cap); cap = round3(c% × OS or current value); none if no cap. */
export function retentionToDate(terms: ClaimTerms, gross: string): string {
  const held = D(gross).times(terms.retentionRatePct).dividedBy(100)
  if (terms.retentionCapPct === null) return r3(held)
  const base = terms.retentionCapBasis === 'CURRENT' ? terms.currentValue : terms.originalSum
  const cap = D(r3(D(base).times(terms.retentionCapPct).dividedBy(100)))
  const rounded = D(r3(held))
  return toMoney(rounded.greaterThan(cap) ? cap : rounded)
}

/**
 * Advance recovery to date (04 §8.5; P5 decision 31), A the net advance and A₀ the advance as invoiced:
 * PRO_RATA min(A, round3(A₀ × G ÷ OS)); FIXED_RATE min(A, round3(rate% × max(0, G − round3(threshold% ×
 * basis)))); MANUAL entered, capped at A. A FINAL certificate recovers the whole (net) advance.
 */
export function recoveryToDate(
  terms: ClaimTerms,
  gross: string,
  kind: 'INTERIM' | 'FINAL',
  manual: string | null | undefined,
): string {
  const a = D(terms.advance)
  if (a.lessThanOrEqualTo(0)) return '0.000'
  if (kind === 'FINAL') return toMoney(a)
  let v: ReturnType<typeof money>
  if (terms.recovery === 'PRO_RATA') {
    // The rate runs on the advance as invoiced; the reduction only lowers the cap below.
    v = D(terms.originalSum).isZero()
      ? a
      : D(r3(D(terms.advanceInvoiced).times(gross).dividedBy(terms.originalSum)))
  } else if (terms.recovery === 'FIXED_RATE') {
    const basis =
      terms.thresholdBasis === 'ORIGINAL_LESS_PROVISIONAL'
        ? D(terms.originalSum).minus(terms.provisionalSums)
        : D(terms.originalSum)
    const threshold = D(r3(basis.times(terms.startThresholdPct).dividedBy(100)))
    v = D(r3(max0(D(gross).minus(threshold)).times(terms.fixedRatePct).dividedBy(100)))
  } else {
    v = max0(D(manual ?? '0'))
  }
  return toMoney(v.greaterThan(a) ? a : v)
}

function figure(toDate: string, previous: string): ClaimFigure {
  return { toDate: toMoney(toDate), previous: toMoney(previous), period: toMoney(D(toDate).minus(previous)) }
}

/** One certificate: gross, deductions, net, VAT and revenue (04 §8.5–8.6). Pure; never throws. */
export function calculateClaim(input: ClaimCalcInput): ClaimCalcResult {
  const { terms } = input
  const retentionComputed = retentionToDate(terms, input.grossToDate)
  const recoveryComputed = recoveryToDate(terms, input.grossToDate, input.kind, input.manualRecoveryToDate)
  const retentionUsed = input.retentionOverride ?? retentionComputed
  const recoveryUsed =
    input.kind === 'FINAL' ? recoveryComputed : (input.recoveryOverride ?? recoveryComputed)

  const gross = figure(input.grossToDate, input.previous.gross)
  const retention = figure(retentionUsed, input.previous.retention)
  const recovery = figure(recoveryUsed, input.previous.recovery)
  const penalty = figure(input.penaltyToDate, input.previous.penalty)

  const net = D(gross.period).minus(retention.period).minus(recovery.period).minus(penalty.period)
  const vat = D(r3(net.times(terms.taxRatePct).dividedBy(100)))
  const errors: ClaimCalcResult['errors'] = []
  if (net.lessThanOrEqualTo(0)) errors.push('NEGATIVE_CERTIFICATE')
  if (D(retention.toDate).lessThan(input.retentionReleased)) errors.push('RETENTION_BELOW_RELEASED')
  if (D(recovery.toDate).greaterThan(D(terms.advance).lessThan(0) ? 0 : terms.advance)) {
    errors.push('RECOVERY_ABOVE_ADVANCE')
  }
  if (D(retention.toDate).greaterThan(gross.toDate)) errors.push('RETENTION_ABOVE_GROSS')
  return {
    gross,
    retentionComputed,
    retention,
    recoveryComputed,
    recovery,
    penalty,
    net: toMoney(net),
    vat: toMoney(vat),
    total: toMoney(net.plus(vat)),
    revenue: toMoney(D(gross.period).minus(penalty.period)),
    errors,
  }
}

/**
 * The Projects journal of a certificate (J1, 04 §8.6 rule 8): Dr retention receivable Δretention,
 * Dr customer advances Δrecovery / Cr contract revenue (Δretention + Δrecovery). A negative delta (a
 * true-up) flips side; zero lines are dropped.
 */
export function certificateJournal(result: Pick<ClaimCalcResult, 'retention' | 'recovery'>): {
  role: 'retention_receivable' | 'customer_advances' | 'contract_revenue'
  debit: string
  credit: string
}[] {
  const lines: {
    role: 'retention_receivable' | 'customer_advances' | 'contract_revenue'
    amount: ReturnType<typeof money>
  }[] = [
    { role: 'retention_receivable', amount: D(result.retention.period) },
    { role: 'customer_advances', amount: D(result.recovery.period) },
    {
      role: 'contract_revenue',
      amount: D(result.retention.period).plus(result.recovery.period).negated(),
    },
  ]
  return lines
    .filter((l) => !l.amount.isZero())
    .map((l) => ({
      role: l.role,
      debit: l.amount.greaterThan(0) ? toMoney(l.amount) : '0.000',
      credit: l.amount.lessThan(0) ? toMoney(l.amount.negated()) : '0.000',
    }))
}
